import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import TopNavBar from './TopNavBar';
import { fetchProductPricesFromCSV } from '../utils/api';
import { findProductById } from '../utils/productHelpers';
import enhancedAnalytics from '../utils/enhancedAnalytics';
import '../FishFinderBuilder.css';

function LandingPage() {
  const navigate = useNavigate();
  
  // Get the product data to get the correct MSRP
  const liveScopeProduct = findProductById("livescope-plus-lvs34");
  const bundleProduct = findProductById("Ultra2 106sv + LVS34 Bundle");
  
  // Track active requests to prevent duplicate API calls
  const activeFetches = useRef(new Set());
  
  const [liveScopePricing, setLiveScopePricing] = useState({
    bestPrice: null,
    msrp: liveScopeProduct?.msrp || null,
    savings: null,
    loading: true,
    error: null
  });

  const [bundlePricing, setBundlePricing] = useState({
    bestPrice: null,
    msrp: bundleProduct?.msrp || null,
    savings: null,
    loading: true,
    error: null
  });

  useEffect(() => {
    // Only proceed if we have the product data and no active fetch for this product
    if (!liveScopeProduct || activeFetches.current.has('livescope')) {
      if (!liveScopeProduct) {
        console.error('LiveScope Plus LVS34 product data not found');
        setLiveScopePricing(prev => ({ 
          ...prev, 
          loading: false, 
          error: 'Product data not found' 
        }));
      }
      return;
    }

    // Fetch LiveScope Plus LVS34 pricing on component mount
    const fetchLiveScopePricing = async () => {
      // Mark this request as active
      activeFetches.current.add('livescope');
      
      try {
        const response = await fetchProductPricesFromCSV(null, 'LiveScope Plus LVS34', true);
        if (response && response.success && response.data && response.data.length > 0) {
          console.log('LiveScope pricing data received:', response.data);
          
          // Find the best (lowest) price
          const prices = response.data.map(item => {
            const priceStr = item.Price || '';
            return parseFloat(priceStr.replace(/[$,]/g, ''));
          }).filter(price => !isNaN(price));
          
          console.log('Parsed prices:', prices);
          
          if (prices.length > 0) {
            const bestPrice = Math.min(...prices);
            const savings = liveScopeProduct.msrp - bestPrice;
            
            console.log(`Best LiveScope price: $${bestPrice}, MSRP: $${liveScopeProduct.msrp}, Savings: $${savings}`);
            
            setLiveScopePricing({
              bestPrice,
              msrp: liveScopeProduct.msrp,
              savings,
              loading: false,
              error: null
            });
          } else {
            console.error('No valid prices found in API response');
            setLiveScopePricing(prev => ({ 
              ...prev, 
              loading: false, 
              error: 'No valid prices found' 
            }));
          }
        } else {
          console.error('No pricing data received from API');
          setLiveScopePricing(prev => ({ 
            ...prev, 
            loading: false, 
            error: 'No pricing data available' 
          }));
        }
      } catch (error) {
        console.error('Error fetching LiveScope pricing:', error);
        setLiveScopePricing(prev => ({ 
          ...prev, 
          loading: false, 
          error: 'Unable to load pricing' 
        }));
      } finally {
        // Remove this request from active fetches
        activeFetches.current.delete('livescope');
      }
    };

    // Add a small delay to prevent rapid-fire requests
    const timeoutId = setTimeout(fetchLiveScopePricing, 100);
    return () => clearTimeout(timeoutId);
  }, [liveScopeProduct]);

  useEffect(() => {
    // Only proceed if we have the bundle product data and no active fetch for this product
    if (!bundleProduct || activeFetches.current.has('bundle')) {
      if (!bundleProduct) {
        console.error('Ultra2 106sv + LVS34 Bundle product data not found');
        setBundlePricing(prev => ({ 
          ...prev, 
          loading: false, 
          error: 'Product data not found' 
        }));
      }
      return;
    }

    // Fetch bundle pricing on component mount
    const fetchBundlePricing = async () => {
      // Mark this request as active
      activeFetches.current.add('bundle');
      
      try {
        const response = await fetchProductPricesFromCSV(null, 'Ultra2 106sv + LVS34 Bundle', true);
        if (response && response.success && response.data && response.data.length > 0) {
          console.log('Bundle pricing data received:', response.data);
          
          // Find the best (lowest) price
          const prices = response.data.map(item => {
            const priceStr = item.Price || '';
            return parseFloat(priceStr.replace(/[$,]/g, ''));
          }).filter(price => !isNaN(price));
          
          console.log('Parsed bundle prices:', prices);
          
          if (prices.length > 0) {
            const bestPrice = Math.min(...prices);
            const savings = bundleProduct.msrp - bestPrice;
            
            console.log(`Best bundle price: $${bestPrice}, MSRP: $${bundleProduct.msrp}, Savings: $${savings}`);
            
            setBundlePricing({
              bestPrice,
              msrp: bundleProduct.msrp,
              savings,
              loading: false,
              error: null
            });
          } else {
            console.error('No valid bundle prices found in API response');
            setBundlePricing(prev => ({ 
              ...prev, 
              loading: false, 
              error: 'No valid prices found' 
            }));
          }
        } else {
          console.error('No bundle pricing data received from API');
          setBundlePricing(prev => ({ 
            ...prev, 
            loading: false, 
            error: 'No pricing data available' 
          }));
        }
      } catch (error) {
        console.error('Error fetching bundle pricing:', error);
        setBundlePricing(prev => ({ 
          ...prev, 
          loading: false, 
          error: 'Unable to load pricing' 
        }));
      } finally {
        // Remove this request from active fetches
        activeFetches.current.delete('bundle');
      }
    };

    // Add a small delay to prevent rapid-fire requests
    const timeoutId = setTimeout(fetchBundlePricing, 150);
    return () => clearTimeout(timeoutId);
  }, [bundleProduct]);



  const handleCustomBuilderClick = (e) => {
    e.preventDefault();
    
    // Track bundle builder start
    enhancedAnalytics.trackEvent('bundle_builder_start', {
      source: 'landing_page',
      button_location: 'hero_cta'
    });
    
    navigate('/custom-builder');
    // Scroll to top after navigation
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handleDealClick = (dealName, retailer, originalPrice, salePrice, dealUrl) => {
    // Track deal clicks with detailed analytics
    enhancedAnalytics.trackDealClick(dealName, retailer, originalPrice, salePrice, dealUrl);
  };

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <div className="hero-section">
        {/* TopNavBar integrated into hero for homepage */}
        <TopNavBar />
        
                  <div className="hero-content">
            <h1>Built by Anglers for Anglers</h1>
            <p>Compare gear. Build your setup. Get the best deal.</p>
            
            {/* First Tool - Custom Bundle Builder Card */}
            <div className="hero-tool-card">
              <a href="/custom-builder" onClick={handleCustomBuilderClick} className="card">
                <div className="card-image-panel">
                  <img src="/images/GPS-GEMU295LSB_0.webp" alt="LiveScope Setup Builder" className="card-image" />
                </div>
                <div className="card-content">
                  <div className="popular-badge">🔥 Most Used Tool</div>
                  <h3 className="card-title">🛠️ Custom Bundle Builder</h3>
                  <p className="card-sub">Build your setup for boat or ice.</p>
                  <span className="card-cta-button">
                    ➡️ Start My Bundle
                  </span>
                </div>
              </a>
            </div>
          </div>
      </div>



      {/* Other Tools Section */}
      <h2 className="what-are-you-looking-for">⬇ Other Tools You Can Use</h2>
      
      <div className="options-grid">
        {/* Live Price Comparison */}
        <Link to="/price-comparison" className="card">
          <div className="card-image-panel">
            <img src="/images/BestPriceCard.png" alt="Price Comparison" className="card-image" />
          </div>
          <div className="card-content">
            <h3 className="card-title">💸 Best Price Finder</h3>
                          <p className="card-sub">Find the lowest price right now! Across Amazon, SCHEELS & more.</p>
            <span className="card-cta-button">
              ➡️ Compare Prices
            </span>
          </div>
        </Link>
        
        {/* Compare Every Screen */}
        <Link to="/compare-screens" className="card">
          <div className="card-image-panel">
            <img src="/images/68798-Chartplotter-Blog-header-image.webp" alt="Screen" className="card-image" />
          </div>
          <div className="card-content">
                        <h3 className="card-title">🖥️ Garmin Screen Finder</h3>
            <p className="card-sub">Filter, compare, and find your perfect Garmin screen.</p>
            <span className="card-cta-button">
              ➡️ See Screen Specs
            </span>
          </div>
        </Link>
        
        {/* Pick Your Battery */}
        <Link to="/select/battery" className="card">
          <div className="card-image-panel">
            <img src="/images/batteries/amped-30ah-battery.webp" alt="Battery" className="card-image" />
          </div>
          <div className="card-content">
            <h3 className="card-title">🔋 Battery Finder</h3>
                          <p className="card-sub">Get the right battery: size, runtime, and match.</p>
            <span className="card-cta-button">
              ➡️ Find My Battery
            </span>
          </div>
        </Link>
      </div>

      {/* Hot Deals Section - Compact Layout */}
      <div className="hot-deals-section compact" id="hot-deals">
        <h2>💰 Today's Best Garmin Deals</h2>
        
        <div className="deals-grid compact">
          {/* Deal 1: Ultra2 106sv + LVS34 Bundle */}
          <Link 
            to="/simple-products/Ultra2 106sv + LVS34 Bundle" 
            className="deal-card compact clickable"
            onClick={() => {
              if (bundlePricing.bestPrice && bundlePricing.msrp) {
                handleDealClick(
                  'Garmin Ultra2 106sv + LVS34 Bundle',
                  'Best Price Available',
                  bundlePricing.msrp,
                  bundlePricing.bestPrice,
                  '/simple-products/Ultra2 106sv + LVS34 Bundle'
                );
              }
            }}
          >
            <div className="deal-image compact">
              <img src="https://scheels.scene7.com/is/image/Scheels/42400001048_11?wid=1200&hei=1200" alt="Garmin Ultra2 106sv + LVS34 Bundle" />
            </div>
            <div className="deal-content compact">
              <h3>Garmin Ultra2 106sv + LVS34 Bundle</h3>
              <div className="deal-features">10" screen + LiveScope Plus</div>
              <div className="deal-pricing">
                {bundlePricing.loading ? (
                  <div className="pricing-loading">Loading pricing...</div>
                ) : bundlePricing.error ? (
                  <div className="pricing-error">Pricing unavailable</div>
                ) : bundlePricing.bestPrice ? (
                  <>
                    <div className="deal-price">${bundlePricing.bestPrice.toFixed(2)}</div>
                    {bundlePricing.savings && bundlePricing.savings > 0 && (
                      <div className="deal-savings-badge">Save ${bundlePricing.savings.toFixed(2)}</div>
                    )}
                  </>
                ) : (
                  <div className="pricing-error">Price N/A</div>
                )}
              </div>
            </div>
          </Link>

          {/* Deal 2: LiveScope Plus LVS34 */}
          <Link 
            to="/simple-products/livescope-plus-lvs34" 
            className="deal-card compact clickable"
            onClick={() => {
              if (liveScopePricing.bestPrice && liveScopePricing.msrp) {
                handleDealClick(
                  'Garmin LiveScope Plus LVS34',
                  'Best Price Available',
                  liveScopePricing.msrp,
                  liveScopePricing.bestPrice,
                  '/simple-products/livescope-plus-lvs34'
                );
              }
            }}
          >
            <div className="deal-image compact">
              <img src="/images/transducers/livescopeplus.jpg" alt="Garmin LiveScope Plus LVS34" />
            </div>
            <div className="deal-content compact">
              <h3>Garmin LiveScope Plus LVS34</h3>
              <div className="deal-features">Most popular forward-facing sonar</div>
              <div className="deal-pricing">
                {liveScopePricing.loading ? (
                  <div className="pricing-loading">Loading pricing...</div>
                ) : liveScopePricing.error ? (
                  <div className="pricing-error">Pricing unavailable</div>
                ) : liveScopePricing.bestPrice ? (
                  <>
                    <div className="deal-price">${liveScopePricing.bestPrice.toFixed(2)}</div>
                    {liveScopePricing.savings && liveScopePricing.savings > 0 && (
                      <div className="deal-savings-badge">Save ${liveScopePricing.savings.toFixed(2)}</div>
                    )}
                  </>
                ) : (
                  <div className="pricing-error">Price N/A</div>
                )}
              </div>
            </div>
          </Link>
        </div>
        
        <div className="view-all-deals compact">
          <Link to="/price-comparison" className="view-all-button">🔍 See All Gear Prices</Link>
        </div>
      </div>


    </div>
  );
}

export default LandingPage; 